import { PauseIcon } from "@phosphor-icons/react";
import { Button, Flex, Typography } from "antd";
import { useAntTheme } from "hooks/useAntTheme";
import SpeakerItem from "pages/media/speaker/components/SpeakerItem";
import { VoiceProfileResponse } from "pages/media/types";
import React, { useEffect, useRef } from "react";
import { getVoicePreviewForLanguage } from "./helpers";
import { MicrophoneButton } from "./MicrophoneButton";
import { SelectedItem } from "./styled";

interface VoiceItemProps extends React.ComponentPropsWithoutRef<"div"> {
  onClick?: () => void;
  isSelected?: boolean;
  currentlyPlayedVoice?: VoiceProfileResponse;
  onClickPlay?: (voice?: VoiceProfileResponse) => void;
  id: string;
  voice: VoiceProfileResponse;
  itemStyle?: React.CSSProperties;
  iconStyle?: React.CSSProperties;
  showTitle?: boolean;
  className?: string;
  language?: string;
}

const VoiceItem: React.FC<VoiceItemProps> = ({
  className,
  onClick: onClickItem,
  isSelected = false,
  onClickPlay: onClickPlayProp,
  currentlyPlayedVoice,
  id,
  voice: currentVoice,
  style,
  itemStyle,
  iconStyle,
  showTitle = true,
  language,
}) => {
  const [playing, setPlaying] = React.useState(false);
  const { theme } = useAntTheme();

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioPath = getVoicePreviewForLanguage(currentVoice, language);

  useEffect(() => {
    // stop playing if another starts
    if (currentlyPlayedVoice?.id && currentlyPlayedVoice?.id !== id) {
      setPlaying(false); // force stop
    }
  }, [currentlyPlayedVoice, id]);

  useEffect(() => {
    if (playing) {
      if (!audioPath) {
        setPlaying(false);
        return;
      }
      // forward to parent
      onClickPlayProp?.(currentVoice);
      if (audioRef.current) {
        // already used , rewind audio
        audioRef.current.currentTime = 0;
      } else {
        // init audio player
        audioRef.current = new Audio(audioPath);
        audioRef.current.addEventListener("ended", () => setPlaying(false));
      }
      audioRef.current?.play().catch(() => console.error); // start playing!
    } else {
      audioRef?.current?.pause();
    }
  }, [playing]);

  return (
    <StyledDiv
      className={`${className} ${isSelected && showTitle ? "selected" : ""}`}
      style={style}
    >
      <SpeakerItem
        style={itemStyle}
        className={isSelected ? "selected" : ""}
        onClick={(e) => {
          e.stopPropagation();
          onClickItem?.();
        }}
      >
        <Flex
          vertical
          justify="center"
          align="center"
          style={{ cursor: "pointer", width: "100%", height: "100%" }}
        >
          {playing ? (
            <Button
              type="text"
              style={{
                background: "transparent",
              }}
              icon={<PauseIcon size={42} color={theme.colorTextDescription} />}
              onClick={(e) => {
                e.stopPropagation();
                return setPlaying((prev) => !prev);
              }}
            />
          ) : (
            <MicrophoneButton
              fillColor={
                iconStyle?.color ??
                (isSelected && !showTitle
                  ? theme.colorText
                  : theme.colorTextDescription)
              }
              iconStyle={{
                width: 42,
                height: 42,
                ...iconStyle,
              }}
              onClick={(e) => {
                e.stopPropagation();
                return setPlaying((prev) => !prev);
              }}
            />
          )}
          {showTitle && (
            <Typography.Text
              style={{
                color: theme.colorTextDescription,
                textAlign: "center",
                margin: "15px 0 0 0",
                padding: "0 10px",
              }}
              title={currentVoice.name}
              ellipsis
            >
              {currentVoice.name}
            </Typography.Text>
          )}
        </Flex>
      </SpeakerItem>
    </StyledDiv>
  );
};

const StyledDiv = SelectedItem("div");

export default VoiceItem;
